import type { ErpModule } from '@/api/platform.api';
import { enabledHeadModuleCodes } from '@/config/moduleEntries';
import { decodeJwt } from '@/utils/jwt';
import { STORAGE_KEYS } from '@/constants/storage';
import {
  getModuleEntryByErpModule,
  isPathAllowedForEntry,
  type ModuleEntry,
} from '@/config/moduleEntries';
import { isGlobalWorkspaceRoute } from '@/constants/globalWorkspaceRoutes';
import { isPlatformAdminRoute } from '@/config/moduleDefaults';

export function readEntryModuleFromToken(accessToken: string | null): ErpModule | null {
  if (!accessToken) return null;
  const claim = decodeJwt(accessToken)?.entryModule;
  return claim ? (claim as ErpModule) : null;
}

export function readStoredEntryModule(): ErpModule | null {
  const raw = sessionStorage.getItem(STORAGE_KEYS.ENTRY_MODULE);
  return raw ? (raw as ErpModule) : null;
}

export function persistEntryModule(entryModule: ErpModule | null | undefined) {
  if (entryModule) {
    sessionStorage.setItem(STORAGE_KEYS.ENTRY_MODULE, entryModule);
  } else {
    sessionStorage.removeItem(STORAGE_KEYS.ENTRY_MODULE);
  }
}

export function readStoredWorkspaceModule(): ErpModule | null {
  const raw = sessionStorage.getItem(STORAGE_KEYS.WORKSPACE_MODULE);
  return raw ? (raw as ErpModule) : null;
}

export function persistWorkspaceModule(module: ErpModule | null | undefined) {
  if (module) {
    sessionStorage.setItem(STORAGE_KEYS.WORKSPACE_MODULE, module);
  } else {
    sessionStorage.removeItem(STORAGE_KEYS.WORKSPACE_MODULE);
  }
}

/** First subscribed head module for default workspace after login. */
export function resolveDefaultWorkspaceModule(enabledModules: string[]): ErpModule | null {
  const codes = enabledHeadModuleCodes(enabledModules);
  return codes.length > 0 ? (codes[0] as ErpModule) : null;
}

export function clearEntryModuleStorage() {
  sessionStorage.removeItem(STORAGE_KEYS.ENTRY_MODULE);
  sessionStorage.removeItem(STORAGE_KEYS.PENDING_ENTRY_MODULE);
  sessionStorage.removeItem(STORAGE_KEYS.WORKSPACE_MODULE);
}

export function getActiveModuleEntry(entryModule: ErpModule | null | undefined): ModuleEntry | undefined {
  if (!entryModule) return undefined;
  return getModuleEntryByErpModule(entryModule);
}

export function isRouteAllowedForEntryModule(pathname: string, entryModule: ErpModule | null | undefined): boolean {
  if (!entryModule) return true;
  if (isGlobalWorkspaceRoute(pathname)) return true;
  if (isPlatformAdminRoute(pathname)) return true;
  const entry = getModuleEntryByErpModule(entryModule);
  if (!entry) return true;
  if (pathname === '/onboarding') return true;
  return isPathAllowedForEntry(pathname, entry);
}

export function getPostLoginPath(entryModule: ErpModule | null | undefined, fallback: string): string {
  if (!entryModule) return fallback;
  return getModuleEntryByErpModule(entryModule)?.homePath ?? fallback;
}
